'use client';

import { useEffect, useRef } from 'react';
import { SILKTIDE_ASSET_VERSION } from './silktide-version';

/**
 * Silktide Consent Initializer (Client Component)
 * 
 * This component initializes Silktide AFTER React hydration completes.
 * This prevents Next.js from removing Silktide's DOM elements during reconciliation.
 * 
 * The library and config are loaded in <head> via silktide-consent.tsx,
 * but initialization is deferred until React is ready.
 */
export default function SilktideConsentInit() {
  const initialized = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (initialized.current) return;
    initialized.current = true;

    function ensureSilktideStylesheet(onReady: () => void) {
      const href = `/static/css/silktide-consent-manager.css?v=${SILKTIDE_ASSET_VERSION}`;
      const existing = document.querySelector(`link[href="${href}"]`) as HTMLLinkElement | null;

      if (existing) {
        // If stylesheet is already loaded, continue immediately.
        if ((existing as any).sheet) {
          onReady();
          return;
        }

        existing.addEventListener('load', onReady, { once: true });
        // Fallback in case the load event is missed.
        setTimeout(onReady, 100);
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.addEventListener('load', onReady, { once: true });
      document.head.appendChild(link);
      // Fallback in case the load event is missed.
      setTimeout(onReady, 100);
    }

    // Wait for everything to be ready
    function initializeSilktide() {
      // Check if library is loaded
      if (typeof window.silktideCookieBannerManager === 'undefined') {
        setTimeout(initializeSilktide, 10);
        return;
      }

      // Check if config is loaded
      if (typeof window.__silktideConfig === 'undefined') {
        setTimeout(initializeSilktide, 10);
        return;
      }

      // Initialize with the stored config
      window.silktideCookieBannerManager.updateCookieBannerConfig(window.__silktideConfig);

      // Defensive visibility guard: some staging environments can retain
      // stale inline `display:none` styles from cached assets.
      setTimeout(() => {
        const suffix = window.__silktideConfig?.bannerSuffix ? `_${window.__silktideConfig.bannerSuffix}` : '';
        const hasInitialChoice = !!window.localStorage.getItem(`silktideCookieBanner_InitialChoice${suffix}`);
        const wrapper = document.getElementById('silktide-wrapper') as HTMLElement | null;
        const banner = document.getElementById('silktide-banner') as HTMLElement | null;

        if (!hasInitialChoice && wrapper) {
          wrapper.style.display = 'flex';
          wrapper.style.visibility = 'visible';
        }

        if (!hasInitialChoice && banner) {
          banner.style.display = 'block';
          banner.style.visibility = 'visible';
          banner.removeAttribute('hidden');
        }
      }, 50);

      // Push stcm_ready event to dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'stcm_ready'
      });

      console.log('[Silktide] Initialized after hydration');
    }

    // Start initialization after hydration and stylesheet load to avoid unstyled banner flashes.
    setTimeout(() => ensureSilktideStylesheet(initializeSilktide), 100);
  }, []);

  // This component doesn't render anything
  // It just manages the Silktide initialization lifecycle
  return null;
}

// Extend window type for TypeScript
declare global {
  interface Window {
    __silktideConfig?: any;
    silktideCookieBannerManager?: any;
    dataLayer: any[];
  }
}
